import { IconContext } from '@phosphor-icons/react';
import classNames from 'classnames';
import { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import styles from './Button.module.scss';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary-dark'
  | 'secondary-accent'
  | 'tertiary'
  | 'tertiary-accent'
  | 'clear'
  | 'outline'
  | 'negative'
  | 'full'
  | 'dash';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  shape?: 'pill';
  ref?: Ref<HTMLButtonElement>;
  children?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  iconWeight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
}

const BUTTON_ICON_SIZE = { xs: 14, sm: 14, md: 16, lg: 16 } as const;

export function Button({
  variant = 'primary',
  size = 'md',
  shape,
  className,
  children,
  startAdornment,
  endAdornment,
  iconWeight = 'bold',
  ref,
  ...props
}: ButtonProps) {
  'use memo';

  const resolvedIconSize = BUTTON_ICON_SIZE[size];

  return (
    <button
      ref={ref}
      className={classNames(styles.button, styles[variant], styles[size], shape && styles[shape], className)}
      {...props}
    >
      <IconContext.Provider value={{ weight: iconWeight, size: resolvedIconSize }}>
        {startAdornment && <span className={styles.adornment}>{startAdornment}</span>}
        {children != null && <span className={styles.label}>{children}</span>}
        {endAdornment && <span className={styles.adornment}>{endAdornment}</span>}
      </IconContext.Provider>
    </button>
  );
}
